/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description Ephemeral controller store for claim post payment interactions
 */
import { create } from 'zustand';
import _ from 'lodash';
import { useShallow } from 'zustand/react/shallow';

const initialState: PostPayment.IClaimStoreValues = {
	controllers: {},
	pendingOpen: {},
};

const useClaimPostPaymentStore = create<PostPayment.IClaimStore>()((set, get) => ({
	...initialState,
	actions: {
		registerController: (claimId, controller) => {
			set((state) => ({
				controllers: {
					...state.controllers,
					[claimId]: controller,
				},
			}));

			if (get().pendingOpen[claimId]) {
				controller.openPostPayment();
				set((state) => ({
					pendingOpen: _.omit(state.pendingOpen, claimId),
				}));
			}
		},
		unregisterController: (claimId) => {
			set((state) => ({
				controllers: _.omit(state.controllers, claimId),
				pendingOpen: _.omit(state.pendingOpen, claimId),
			}));
		},
		requestOpen: (claimId) => {
			const controller = get().controllers[claimId];
			if (controller) {
				controller.openPostPayment();
				return;
			}

			set((state) => ({
				pendingOpen: {
					...state.pendingOpen,
					[claimId]: true,
				},
			}));
		},
	},
}));

const useClaimPostPaymentStoreValues = (): PostPayment.IClaimStoreValues =>
	useClaimPostPaymentStore(useShallow((state) => _.omit(state, ['actions'])));
const useClaimPostPaymentStoreActions = (): PostPayment.IClaimStoreActions =>
	useClaimPostPaymentStore(useShallow((state) => state.actions));

export { useClaimPostPaymentStoreValues, useClaimPostPaymentStoreActions };
export default useClaimPostPaymentStore;
